import { format, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { TRANSACTION_STATUS_OPTIONS } from '../../constants';
import type { Transaction, TransactionStatus } from '../../types';
import { normalizeDateString } from '../../utils/formatters';

interface TransactionBasicsFormProps {
  transaction?: Partial<Transaction> | null;
  onNext: (data: any) => void;
  onBack: () => void;
}

const TransactionBasicsForm: React.FC<TransactionBasicsFormProps> = ({
  transaction,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState({
    property_address: transaction?.property_address || '',
    sale_price: transaction?.sale_price?.toString() || '',
    closing_date: normalizeDateString(transaction?.closing_date),
    disbursement_date: normalizeDateString(transaction?.disbursement_date),
    settlement_agent_name: transaction?.settlement_agent_name || '',
    settlement_agent_address: transaction?.settlement_agent_address || '',
    settlement_agent_contact: transaction?.settlement_agent_contact || '',
    file_number: transaction?.file_number || '',
    loan_number: transaction?.loan_number || '',
    status: (transaction?.status || 'draft') as TransactionStatus,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [closingDateOpen, setClosingDateOpen] = useState(false);
  const [disbursementDateOpen, setDisbursementDateOpen] = useState(false);

  // Update form when transaction data loads (for edit mode)
  useEffect(() => {
    if (transaction) {
      setFormData({
        property_address: transaction.property_address || '',
        sale_price: transaction.sale_price?.toString() || '',
        closing_date: normalizeDateString(transaction.closing_date),
        disbursement_date: normalizeDateString(transaction.disbursement_date),
        settlement_agent_name: transaction.settlement_agent_name || '',
        settlement_agent_address: transaction.settlement_agent_address || '',
        settlement_agent_contact: transaction.settlement_agent_contact || '',
        file_number: transaction.file_number || '',
        loan_number: transaction.loan_number || '',
        status: (transaction.status || 'draft') as TransactionStatus,
      });
    }
  }, [transaction]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.property_address) {
      newErrors.property_address = 'Property address is required';
    }

    if (formData.sale_price && Number.parseFloat(String(formData.sale_price)) <= 0) {
      newErrors.sale_price = 'Sale price must be greater than 0';
    }

    if (formData.closing_date) {
      const closingDateOnly = new Date(formData.closing_date);
      const todayOnly = new Date();
      todayOnly.setHours(0, 0, 0, 0);
      if (closingDateOnly < todayOnly) {
        newErrors.closing_date = 'Closing date cannot be in the past';
      }
    }

    if (!formData.file_number) {
      newErrors.file_number = 'File number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Format data for API
      const data = {
        property_address: formData.property_address,
        sale_price: formData.sale_price
          ? Number.parseFloat(String(formData.sale_price))
          : undefined,
        closing_date: formData.closing_date || undefined,
        disbursement_date: formData.disbursement_date || undefined,
        settlement_agent_name: formData.settlement_agent_name || undefined,
        settlement_agent_address: formData.settlement_agent_address || undefined,
        settlement_agent_contact: formData.settlement_agent_contact || undefined,
        file_number: formData.file_number,
        loan_number: formData.loan_number || undefined,
        status: formData.status,
      };

      onNext(data);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Property Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Property Information</h3>

          <div className="space-y-2">
            <Label htmlFor="propertyAddress">Property Address *</Label>
            <Textarea
              id="propertyAddress"
              rows={2}
              value={formData.property_address}
              onChange={(e) => handleInputChange('property_address', e.target.value)}
              placeholder="123 Main Street, Unit 4B&#10;Denver, CO 80202"
              className={errors.property_address ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              Enter full address on first line, city/state/zip on second line
            </p>
            {errors.property_address && (
              <p className="text-sm text-destructive">{errors.property_address}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="salePrice">Sale Price</Label>
            <Input
              id="salePrice"
              type="number"
              step="0.01"
              value={formData.sale_price}
              onChange={(e) => handleInputChange('sale_price', e.target.value)}
              placeholder="450000"
              className={errors.sale_price ? 'border-destructive' : ''}
            />
            {errors.sale_price && <p className="text-sm text-destructive">{errors.sale_price}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="closingDate">Closing Date</Label>
            <Popover open={closingDateOpen} onOpenChange={setClosingDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="closingDate"
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !formData.closing_date && 'text-muted-foreground',
                    errors.closing_date && 'border-destructive',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.closing_date
                    ? format(parse(formData.closing_date, 'yyyy-MM-dd', new Date()), 'MMMM d, yyyy')
                    : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  captionLayout="dropdown"
                  fromYear={new Date().getFullYear()}
                  toYear={new Date().getFullYear() + 5}
                  selected={
                    formData.closing_date
                      ? parse(formData.closing_date, 'yyyy-MM-dd', new Date())
                      : undefined
                  }
                  onSelect={(date) => {
                    handleInputChange('closing_date', date ? format(date, 'yyyy-MM-dd') : '');
                    setClosingDateOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
            {errors.closing_date && (
              <p className="text-sm text-destructive">{errors.closing_date}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="disbursementDate">Disbursement Date</Label>
            <Popover open={disbursementDateOpen} onOpenChange={setDisbursementDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="disbursementDate"
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !formData.disbursement_date && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.disbursement_date
                    ? format(
                        parse(formData.disbursement_date, 'yyyy-MM-dd', new Date()),
                        'MMMM d, yyyy',
                      )
                    : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  captionLayout="dropdown"
                  fromYear={new Date().getFullYear()}
                  toYear={new Date().getFullYear() + 5}
                  selected={
                    formData.disbursement_date
                      ? parse(formData.disbursement_date, 'yyyy-MM-dd', new Date())
                      : undefined
                  }
                  onSelect={(date) => {
                    handleInputChange('disbursement_date', date ? format(date, 'yyyy-MM-dd') : '');
                    setDisbursementDateOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Settlement Agent Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Settlement Agent</h3>

          <div className="space-y-2">
            <Label htmlFor="settlementAgentName">Agent Name</Label>
            <Input
              id="settlementAgentName"
              type="text"
              value={formData.settlement_agent_name}
              onChange={(e) => handleInputChange('settlement_agent_name', e.target.value)}
              placeholder="Peak Title Company"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="settlementAgentAddress">Agent Address</Label>
            <Input
              id="settlementAgentAddress"
              type="text"
              value={formData.settlement_agent_address}
              onChange={(e) => handleInputChange('settlement_agent_address', e.target.value)}
              placeholder="456 Title Ave, City, State 80202"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="settlementAgentContact">Contact Information</Label>
            <Input
              id="settlementAgentContact"
              type="text"
              value={formData.settlement_agent_contact}
              onChange={(e) => handleInputChange('settlement_agent_contact', e.target.value)}
              placeholder="303-555-0100"
            />
          </div>
        </div>
      </div>

      {/* File Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">File Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label htmlFor="fileNumber">File Number *</Label>
            <Input
              id="fileNumber"
              type="text"
              value={formData.file_number}
              onChange={(e) => handleInputChange('file_number', e.target.value)}
              placeholder="PT-2025-001234"
              className={errors.file_number ? 'border-destructive' : ''}
            />
            {errors.file_number && <p className="text-sm text-destructive">{errors.file_number}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="loanNumber">Loan Number</Label>
            <Input
              id="loanNumber"
              type="text"
              value={formData.loan_number}
              onChange={(e) => handleInputChange('loan_number', e.target.value)}
              placeholder="LN-2025-001234"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value: TransactionStatus) =>
                setFormData((prev) => ({ ...prev, status: value }))
              }
            >
              <SelectTrigger id="status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {TRANSACTION_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button type="button" variant="outline" onClick={onBack}>
          Cancel
        </Button>
        <Button type="submit">Next: Loan Information</Button>
      </div>
    </form>
  );
};

export default TransactionBasicsForm;
